'use client';

import type { FC, ReactNode } from 'react';
import { SkinImage } from './ui/SkinImage';
import { UserAvatar } from './UserAvatar/UserAvatar';
import { useDisplayUserName } from '../hooks/useDisplayUserName';
import { cn } from '../utils/cn';
import { rarityGradient } from '../utils/rarityGradient';

interface WinnerRowPrize {
  name: string;
  imageUrl?: string | null;
  rarityColor?: string | null;
  tinted?: boolean;
}

interface WinnerRowProps {
  userId: string | number;
  firstName?: string | null;
  lastName?: string | null;
  level?: number;
  prize?: WinnerRowPrize;
  meta: ReactNode;
  thirdLineHasIcon?: boolean;
  surface?: 'card' | 'plain';
  className?: string;
  onClick: () => void;
}

export const WinnerRow: FC<WinnerRowProps> = ({
  userId,
  firstName,
  lastName,
  level,
  prize,
  meta,
  thirdLineHasIcon = true,
  surface = 'card',
  className,
  onClick,
}) => {
  const displayUserName = useDisplayUserName();

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-3',
        surface === 'card' && 'rounded-2xl bg-fill-tertiary',
        'px-3.5 py-3 text-start transition active:opacity-80',
        className,
      )}
    >
      <UserAvatar userId={userId} firstName={firstName} lastName={lastName} level={level} size="m" />
      <div className="min-w-0 flex-1 leading-tight">
        <div className="truncate text-base font-semibold text-label-primary">
          {displayUserName(firstName, lastName)}
        </div>
        {prize && <div className="mt-0.5 truncate text-sm text-label-secondary">{prize.name}</div>}
        <div
          className={cn(
            'mt-0.5',
            thirdLineHasIcon && 'inline-flex items-center gap-1',
            'truncate text-xs text-label-tertiary',
          )}
        >
          {meta}
        </div>
      </div>
      {prize && (
        <div
          className={cn(
            'relative h-16 w-16 shrink-0 overflow-hidden rounded-xl',
            prize.tinted === false && 'bg-fill-secondary',
          )}
          style={prize.tinted === false ? undefined : { background: rarityGradient(prize.rarityColor) }}
        >
          <SkinImage
            name={prize.name}
            src={prize.imageUrl}
            className="absolute inset-0"
            imgClassName="h-full w-full object-contain p-0.5"
          />
        </div>
      )}
    </button>
  );
};
